import prettyBytes from 'pretty-bytes';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import SkeletonMyReportPage from './SkeletonMyReportPage';

import { IconManager } from '@speeki/icons';
import { StorageManager } from '@speeki/storage-manager';

import { ContainerSection } from '@components/_global/GlobalStyle/Global.styled';
import { Container } from '@components/ions/layouts';
import { DefaultLayout } from '@components/templates/DefaultLayout/DefaultLayout';

import {
    ReportCaseDetailsDocument,
    ReportCaseDetailsUnloggedDocument,
    ReportFileDocument,
} from '@graphql/generated/graphql';

import { getPath } from '@services/RouteService/paths';

import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { client } from '@boot/apolloClient';
import {
    authVar,
    currentLanguageVar,
    guestPinVar,
    resetReporterDetails,
} from '@boot/reactiveVariables';
import { useMyReportChat } from '@domains/cases/pages/MyReportPage/useMyReportChat';
import { ChatElement } from '@domains/Chat/ChatElement/ChatElement';
import { REPORT_CHAT_SETTINGS } from '@domains/Chat/ChatElement/reportChatSettings';

import {
    AnswerMessage,
    ButtonIconTextElement,
    ButtonTextWrapper,
    ButtonWrapper,
    EmptyFileListInfo,
    FileName,
    FileSize,
    HeaderText,
    QuestionMessage,
    ReportID,
    ReportMessagesBox,
    ReportStatus,
    ReportUploadFiles,
    ReportWrapper,
    Status,
    StyledContainerInner,
    StyledContainerSection,
    UploadFile,
} from './MyReportPage.styled';

export const MyReportPage = () => {
    const { caseId } = useParams<{ caseId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const pin = useReactiveVar(guestPinVar);

    const tenantUuid = StorageManager.getValue('tenantUuid') || '';

    const { isGuest, isLoggedIn } = useReactiveVar(authVar);

    const { data: guestData, loading: guestLoading } = useQuery(
        ReportCaseDetailsDocument,
        {
            context: {
                notifyOnError: true,
            },
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                client.cache.modify({
                    id: `CaseReport:${client.cache.identify({
                        __ref: data.case.uuid,
                    })}`,
                    fields: {
                        isNewMessageFromAdmin() {
                            return false;
                        },
                    },
                });
            },
            onError: () => navigate(getPath({ path: '/incidents' })),
            skip: !isLoggedIn || (!pin && isGuest),
            variables: {
                input: { caseId, pinCode: pin },
                targetLanguage: currentLanguageVar(),
            },
        },
    );

    const { data: unloggedData, loading: unloggedLoading } = useQuery(
        ReportCaseDetailsUnloggedDocument,
        {
            context: {
                notifyOnError: true,
            },
            onError: () =>
                navigate(getPath({ path: '/auth/see-my-incidents' })),
            skip: isLoggedIn || !pin,
            variables: {
                input: {
                    caseId: caseId ?? '',
                    pinCode: pin,
                    tenantUuid,
                },
                targetLanguage: currentLanguageVar(),
            },
        },
    );

    const [getReportFile] = useLazyQuery(ReportFileDocument, {
        context: {
            notifyOnError: true,
        },
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            window.open(data.file.url, '_blank');
        },
    });

    const caseUuid = guestData?.case.uuid ?? unloggedData?.case.uuid;
    const files = guestData?.case.files ?? unloggedData?.case.files;
    const messages =
        guestData?.case.conversationMessages ??
        unloggedData?.case.conversationMessages;
    const status = guestData?.case.status ?? unloggedData?.case.status;
    const chatRoomSecret =
        guestData?.case.chatRoomSecret ?? unloggedData?.case.chatRoomSecret;
    const firebaseToken =
        guestData?.case.firebaseToken ?? unloggedData?.case.firebaseToken;

    const { chatId, isChatInit } = useMyReportChat(
        caseUuid,
        chatRoomSecret,
        firebaseToken,
    );

    const handleOpenFile = (uuid: string) => {
        if (caseUuid)
            void getReportFile({
                variables: {
                    input: {
                        uuid,
                        caseUuid,
                    },
                },
            });
    };

    const loading = guestLoading || unloggedLoading;

    if (loading) return <SkeletonMyReportPage />;

    if ((!pin && !isLoggedIn) || (!pin && isGuest))
        return (
            <Navigate
                to={getPath({
                    path: isLoggedIn ? '/incidents' : '/auth/see-my-incidents',
                })}
            />
        );

    return (
        <DefaultLayout headerProps={{ withLoginActions: true }}>
            <>
                <Container>
                    <StyledContainerInner>
                        <ContainerSection>
                            <ReportID data-testid="DMHoNgu">{caseId}</ReportID>
                        </ContainerSection>
                        {!isLoggedIn && (
                            <ButtonWrapper>
                                <ButtonIconTextElement
                                    iconProps={{
                                        icon: 'SvgAddNew',
                                        size: 24,
                                    }}
                                    onPress={() => {
                                        navigate(
                                            getPath({
                                                path: '/auth/start-new-report',
                                            }),
                                        );
                                        resetReporterDetails();
                                    }}
                                    testId="_9DgNaK"
                                >
                                    <ButtonTextWrapper>
                                        {t('CREATE_NEW_REPORT')}
                                    </ButtonTextWrapper>
                                </ButtonIconTextElement>
                            </ButtonWrapper>
                        )}
                        <StyledContainerSection>
                            <ReportStatus>
                                {t('STATUS')}:
                                <Status>{t(status || '').toUpperCase()}</Status>
                            </ReportStatus>
                        </StyledContainerSection>
                    </StyledContainerInner>

                    <ReportWrapper>
                        <ReportMessagesBox className="htmlToImageIgnore">
                            <HeaderText>{t('REPORT_YOUR')}</HeaderText>
                            {messages?.map((message) => {
                                return message.isAssistant ? (
                                    <QuestionMessage key={message.uuid}>
                                        {message.textTranslated}
                                    </QuestionMessage>
                                ) : (
                                    <AnswerMessage key={message.uuid}>
                                        {message.text}
                                    </AnswerMessage>
                                );
                            })}
                        </ReportMessagesBox>
                        <ReportUploadFiles>
                            <HeaderText>{t('UPLOADED_FILES')}</HeaderText>
                            {files?.length ? (
                                files?.map((file) => (
                                    <UploadFile
                                        key={file.uuid}
                                        onClick={() =>
                                            // @ts-expect-error TODO: fix file union, "files" should be ReportCaseDetails_case_files[] | ReportCaseDetailsUnlogged_case_files[]
                                            file?.url
                                                ? window.open(
                                                      // @ts-expect-error see above
                                                      file.url,
                                                      '_blank',
                                                  )
                                                : handleOpenFile(file.uuid)
                                        }
                                    >
                                        <IconManager
                                            icon={'SvgImage24'}
                                            size={16}
                                            themeColors={{
                                                default: {
                                                    'color-1': 'main.tertiary',
                                                },
                                            }}
                                        />
                                        <FileName>{file.name}</FileName>
                                        {file.size && (
                                            <FileSize>
                                                {'• ' + prettyBytes(file.size)}
                                            </FileSize>
                                        )}
                                    </UploadFile>
                                ))
                            ) : (
                                <EmptyFileListInfo>
                                    {t('UPLOADED_FILES_EMPTY')}
                                </EmptyFileListInfo>
                            )}
                        </ReportUploadFiles>
                    </ReportWrapper>
                </Container>
                {isChatInit && chatId && (
                    <ChatElement
                        {...REPORT_CHAT_SETTINGS}
                        chatId={chatId}
                        defaultOpenChat={false}
                        participantName={t('CASE_MANAGER')}
                    />
                )}
            </>
        </DefaultLayout>
    );
};
